/**
 * V70 엔글 마블 V2 — 메인 게임 화면
 *
 * V2 변경:
 *   - 중앙 "nGle Works" 모던 타이포 (이미지 제거)
 *   - 주사위 pip 눈금 1~6 (CSS Grid)
 *   - 캐릭터 플레이어 마커 (SVG)
 *   - HUD: 바퀴/완주보상/아이템/무인도 상태 표시
 *   - 가속 사용 토글 (주사위 굴리기 전)
 *   - 다중 탭 Shadow 모드 (useTabLock)
 *   - 게임 방법 모달 (Help)
 *   - 텍스트 화살표 → 제거
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Dices, Shield, Zap, Flag, ArrowLeft, RefreshCw, PlusCircle, HelpCircle } from 'lucide-react';
import { useCurrentUser } from '../../../shared/hooks/useCurrentUser';
import { marbleApi, V2CorruptError } from './mock/serverApi';
// V70 2026-10-19 — 서버 전환 감지 (안내 문구 조건부 분기)
import { isPhase3cServerEnabled } from '../shared/phase3cFlag';
import { cellLabel, positionToGrid, GRID_SIZE, safeCornerLabel } from './engine/boardLayout';
import type {
  BoardCell, LeaderboardRow, MyState, RollDiceResponse, DailyDiceStatus, InProgressEventState,
} from './engine/types';
import { MARBLE_GLOBAL_CONFIG, V2_SAFE_ROLES, LAP_REWARD, TRAP_CONFIG } from './config/eventConfig';
import { trapAttemptButtonLabel, trapAttemptHudValue } from './trapAttempt';
import { MarbleEventModal } from './events/MarbleEventModals';
import { MarbleHelpModal } from './MarbleHelpModal';
import { useTabLock } from './hooks/useTabLock';
import { CellIcon } from './components/IconSet';
import { Dice3D } from './components/Dice3D';
import { PlayerPawn } from './components/PlayerPawn';
import { COLORS } from './components/tokens';

type ActivePending = InProgressEventState & { _instanceId: string };
type ChoiceReqMap = { [instanceId: string]: string };

function makeInstanceId(): string {
  return `inst-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

type Phase = 'loading' | 'ready' | 'animating' | 'escape-cheer' | 'lap-cheer' | 'trap-entered' | 'event' | 'result';
const STEP_MS = 150;
const ESCAPE_CHEER_MS = 1500;
const TRAP_FAIL_TOAST_MS = 2500;
function sleep(ms: number): Promise<void> { return new Promise((r) => setTimeout(r, ms)); }

interface EscapeCheerState {
  escaped: boolean;         // 탈출 성공 여부
  wasGuaranteed: boolean;   // 3회 보장 발동 여부
  diceFace: number;         // 실제 주사위 눈
  trapFailsAfter: number;   // 실패 후 trapFails (성공 시 0)
}

interface LapCheerState {
  amount: number;           // 바퀴 보상 총합
  laps: number;             // 바퀴 수
  onContinue: () => void;   // "계속하기" 버튼 핸들러 (사용자 지시 §1 — 자동 종료 X)
}

interface TrapEnteredState {
  onContinue: () => void;   // "확인" 버튼 핸들러 (사용자 지시 §QA4 §1 — 자동 종료 X)
}

/**
 * V70G8K — 결과 공개 전까지 화면 반영을 보류할 이벤트 종류.
 *   E7 홀짝: 서버 응답 즉시 HUD 점수·순위·리더보드가 바뀌어 추첨 애니메이션 중 결과가 드러나던 문제.
 *   (다른 선택형 이벤트는 기존 표시 흐름 유지 — 이번 지시 범위)
 */
const DEFER_SCORE_REVEAL_KINDS: ReadonlySet<string> = new Set(['E7']);
export function shouldDeferScoreReveal(kind: string): boolean {
  return DEFER_SCORE_REVEAL_KINDS.has(kind);
}
interface DeferredReveal {
  instanceId: string;
  state?: MyState;
  diceStatus?: DailyDiceStatus;
}

interface TrapFailToastState {
  diceFace: number;
  trapFailsAfter: number;   // 서버 응답 후 state.trapFails (1 또는 2)
}

export function MarbleGamePage() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [phase, setPhase] = useState<Phase>('loading');
  const [myState, setMyState] = useState<MyState | null>(null);
  const [board, setBoard] = useState<BoardCell[]>([]);
  const [diceStatus, setDiceStatus] = useState<DailyDiceStatus | null>(null);
  const [animPos, setAnimPos] = useState<number | null>(null);
  const [lastRoll, setLastRoll] = useState<RollDiceResponse | null>(null);
  const [activePending, setActivePending] = useState<ActivePending | null>(null);
  const choiceReqRef = useRef<ChoiceReqMap>({});
  const [top5, setTop5] = useState<LeaderboardRow[]>([]);
  const [myRank, setMyRank] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [diceAnimValue, setDiceAnimValue] = useState<number | null>(null);
  const diceAnimRef = useRef<number | null>(null);
  const [useSpeedBoost, setUseSpeedBoost] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [previousScore, setPreviousScore] = useState<number | null>(null);
  const [dataCorrupt, setDataCorrupt] = useState(false);
  const [escapeCheer, setEscapeCheer] = useState<EscapeCheerState | null>(null);
  const [lapCheer, setLapCheer] = useState<LapCheerState | null>(null);
  const [trapEntered, setTrapEntered] = useState<TrapEnteredState | null>(null);
  const [trapFailToast, setTrapFailToast] = useState<TrapFailToastState | null>(null);
  const trapFailToastTimerRef = useRef<number | null>(null);

  // 다중 탭 Lock (H1)
  const tabLock = useTabLock(user?.profile.id || null);

  const loadAll = useCallback(async () => {
    if (!user) return;
    setDataCorrupt(false);
    try {
      // 보드/리더보드/주사위는 다른 사용자 레코드도 접근하므로 손상 체크는 getMyState 에만 집중
      const b = await marbleApi.getBoard();
      let myStateResult: MyState | null = null;
      try {
        myStateResult = await marbleApi.getMyState(user.profile.id, (user.profile as any).nameKo || user.profile.displayName || '', user.profile.orgUnitId);
      } catch (err) {
        if ((err as V2CorruptError)?.isV2Corrupt) {
          setDataCorrupt(true);
          setBoard(b.cells);
          setPhase('ready');
          return;
        }
        throw err;
      }
      const [lb, ds] = await Promise.all([
        marbleApi.getLeaderboard(user.profile.id),
        marbleApi.getDailyDiceStatus(user.profile.id),
      ]);
      setBoard(b.cells);
      setMyState(myStateResult);
      setTop5(lb.rows.slice(0, 5));
      setMyRank(lb.self?.rank ?? null);
      setDiceStatus(ds);
      setPhase(myStateResult.inProgressEvent ? 'event' : 'ready');
      if (myStateResult.inProgressEvent) {
        const inst: ActivePending = { ...myStateResult.inProgressEvent, _instanceId: makeInstanceId() };
        setActivePending(inst);
      }
    } catch (e: unknown) {
      setError((e as Error)?.message || '로드 실패');
      setPhase('ready');
    }
  }, [user]);

  useEffect(() => { void loadAll(); }, [loadAll]);

  const playDiceSpin = useCallback((finalValue: number) => {
    return new Promise<void>((resolve) => {
      let count = 0;
      const total = 10;
      diceAnimRef.current = window.setInterval(() => {
        count += 1;
        setDiceAnimValue(Math.floor(Math.random() * 6) + 1);
        if (count >= total) {
          if (diceAnimRef.current != null) window.clearInterval(diceAnimRef.current);
          setDiceAnimValue(finalValue);
          resolve();
        }
      }, 60);
    });
  }, []);

  const animateMove = useCallback(async (from: number, to: number) => {
    let cur = from;
    setAnimPos(cur);
    while (cur !== to) {
      cur = (cur + 1) % MARBLE_GLOBAL_CONFIG.boardSize;
      setAnimPos(cur);
      await sleep(STEP_MS);
    }
  }, []);

  useEffect(() => () => {
    if (diceAnimRef.current != null) window.clearInterval(diceAnimRef.current);
  }, []);

  const refreshLeaderboard = useCallback(async () => {
    if (!user) return;
    try {
      const lb = await marbleApi.getLeaderboard(user.profile.id);
      setTop5(lb.rows.slice(0, 5));
      setMyRank(lb.self?.rank ?? null);
    } catch { /* ignore */ }
  }, [user]);

  const handleRoll = useCallback(async () => {
    if (!user || !myState) return;
    if (dataCorrupt) {
      setError('저장된 데이터에 문제가 있어 플레이할 수 없습니다. 관리자에게 문의하세요.');
      return;
    }
    if (!tabLock.isActive) {
      setError('다른 탭에서 플레이 중입니다. 제어권을 가져온 후 다시 시도하세요.');
      return;
    }
    setError(null);
    // §QA4 §2: 다음 roll 시작 시 이전 토스트 즉시 정리 (타이머 중복 방지)
    if (trapFailToastTimerRef.current != null) {
      window.clearTimeout(trapFailToastTimerRef.current);
      trapFailToastTimerRef.current = null;
    }
    setTrapFailToast(null);
    setPreviousScore(myState.totalScore);
    setPhase('animating');
    setDiceAnimValue(null);

    // 탈출 판별용 state 캡처 (문자열 파싱 금지, 구조화 state 기반 — 사용자 지시 §2)
    const wasTrapped = myState.isTrapped;
    const prevTrapFails = myState.trapFails;

    const willUseSpeed = useSpeedBoost && myState.items.speedBoosts > 0 && !myState.isTrapped;
    setUseSpeedBoost(false);
    try {
      const reqId = `roll-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const resp = await marbleApi.rollDice(user.profile.id, { clientRequestId: reqId, useSpeedBoost: willUseSpeed });
      await playDiceSpin(resp.dice);

      // 상태 재조회 — 서버가 결정한 isTrapped/trapFails 반영
      const [s, ds] = await Promise.all([
        marbleApi.getMyState(user.profile.id, (user.profile as any).nameKo || user.profile.displayName || '', user.profile.orgUnitId),
        marbleApi.getDailyDiceStatus(user.profile.id),
      ]);

      // §QA4 §2: 탈출 실패 — 결과 모달 skip, 토스트 분기 (문자열 파싱 X, state 비교)
      const trapFailed = wasTrapped && s.isTrapped && resp.from === resp.to;

      // 탈출 시도였던 경우: 성공/보장만 escape-cheer, 실패는 아래 토스트 분기
      if (wasTrapped && !trapFailed) {
        const wasGuaranteed = prevTrapFails >= 2;  // 3차 보장 발동
        setEscapeCheer({
          escaped: true, wasGuaranteed, diceFace: resp.dice,
          trapFailsAfter: s.trapFails,
        });
        setPhase('escape-cheer');
        await sleep(ESCAPE_CHEER_MS);
        setEscapeCheer(null);
      }

      // 이동 애니 (실패 시 skip, from===to)
      if (resp.from !== resp.to) {
        await animateMove(resp.from, resp.to);
      }

      // §QA3 §1: 바퀴 완주 overlay — 사용자가 "계속하기" 눌러야 다음 이벤트로 넘어감
      if (resp.lapsReward && resp.lapsReward > 0) {
        const lapsReward = resp.lapsReward;
        // ISSUE-03: 완주 보너스는 LapRewardOverlay 로 사용자에게 이미 노출됨.
        //           이후 결과 모달의 "이전 점수" 는 완주 보너스 반영 후 값이어야 수지가 맞는다.
        //           (previousScore + 이번 변동(이벤트 보상 delta) = 현재 점수)
        //           roll 시작 시점의 totalScore 에 lapsReward 를 더해 보정 — 보너스 중복 지급은 아님 (UI 표시용 조정).
        setPreviousScore(prev => (prev ?? myState.totalScore) + lapsReward);
        await new Promise<void>((resolve) => {
          setLapCheer({
            amount: lapsReward,
            laps: Math.round(lapsReward / LAP_REWARD),
            onContinue: () => {
              setLapCheer(null);
              resolve();
            },
          });
          setPhase('lap-cheer');
        });
      }

      // §QA4 §1: 무인도 최초 진입 — 전용 overlay (사용자 "확인" 눌러야 진행)
      const trapEnteredNow = !wasTrapped && s.isTrapped
        && resp.cellKind === 'safe' && resp.to === V2_SAFE_ROLES.TRAP;
      if (trapEnteredNow) {
        await new Promise<void>((resolve) => {
          setTrapEntered({
            onContinue: () => { setTrapEntered(null); resolve(); },
          });
          setPhase('trap-entered');
        });
      }

      setLastRoll(resp);
      setMyState(s);
      setDiceStatus(ds);
      setAnimPos(null);

      // §QA4 §2: 탈출 실패 → 결과 모달 skip, 토스트 2.5초 후 ready
      if (trapFailed) {
        setTrapFailToast({ diceFace: resp.dice, trapFailsAfter: s.trapFails });
        trapFailToastTimerRef.current = window.setTimeout(() => {
          setTrapFailToast(null);
          trapFailToastTimerRef.current = null;
        }, TRAP_FAIL_TOAST_MS);
        setPhase('ready');
        void refreshLeaderboard();
        return;
      }

      // §QA3 §1 보완: START 도착 (safe + lapsReward 만, 이벤트 보상 0, 선택 X) → 추가 "0점" 결과 모달 skip
      // §QA4 §1 추가: trap-entered 분기도 결과 모달 skip (overlay 가 전담)
      const isSafeStartOnly = resp.cellKind === 'safe' && !resp.pending && !resp.immediate?.shieldConsumed && (resp.immediate?.delta ?? 0) === 0 && (resp.lapsReward ?? 0) > 0;
      if (resp.pending) {
        const inst: ActivePending = { ...resp.pending, _instanceId: makeInstanceId() };
        setActivePending(inst);
        setPhase('event');
      } else if (trapEnteredNow || isSafeStartOnly) {
        setPhase('ready');
        void refreshLeaderboard();
      } else {
        setPhase('result');
        void refreshLeaderboard();
      }
    } catch (e: unknown) {
      setError((e as Error)?.message || '주사위 실패');
      setPhase('ready');
      setAnimPos(null);
      setEscapeCheer(null);
      setLapCheer(null);
      setTrapEntered(null);
    }
  }, [user, myState, dataCorrupt, playDiceSpin, animateMove, refreshLeaderboard, useSpeedBoost, tabLock.isActive]);

  // V70G8K — 결과 공개 전 점수 선노출 방지: 서버 확정 상태를 보류했다가 공개 시점에 한 번만 반영
  const deferredRevealRef = useRef<DeferredReveal | null>(null);
  //   모달은 onSubmit(서버 정산 + 상태 재조회)이 끝난 뒤에야 결과를 받아 결과 화면으로 넘어가므로,
  //   공개 시점에는 보류 상태가 항상 준비돼 있다. 반영은 1회만 (ref 비움).
  const applyDeferredReveal = useCallback((): MyState | null => {
    const held = deferredRevealRef.current;
    if (!held) return null;
    deferredRevealRef.current = null;
    if (held.state) setMyState(held.state);
    if (held.diceStatus) setDiceStatus(held.diceStatus);
    void refreshLeaderboard();
    return held.state ?? null;
  }, [refreshLeaderboard]);
  useEffect(() => () => { deferredRevealRef.current = null; }, []);

  const handleNextRoll = useCallback(() => {
    setLastRoll(null);
    setDiceAnimValue(null);
    setPreviousScore(null);
    setPhase('ready');
  }, []);

  const [qaBusy, setQaBusy] = useState<null | 'reset' | 'refill'>(null);
  const handleQaReset = useCallback(async () => {
    if (!user) return;
    if (qaBusy) return;
    if (!window.confirm('본인 상태를 리셋하시겠습니까? 게임 상태와 현재 시즌 리더보드 기록이 삭제됩니다. 계속하시겠습니까?')) return;
    setQaBusy('reset');
    try {
      const reqId = `qa-reset-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      await marbleApi.qaResetMyState(user.profile.id, reqId);
      await loadAll();
    } catch (e) {
      console.error('[QA][reset]', e);
      window.alert('리셋 실패: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setQaBusy(null);
    }
  }, [user, loadAll, qaBusy]);
  const handleQaRefill = useCallback(async () => {
    if (!user) return;
    if (qaBusy) return;
    setQaBusy('refill');
    try {
      const reqId = `qa-refill-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      await marbleApi.qaRefillDice(user.profile.id, 10, reqId);
      await loadAll();
    } catch (e) {
      console.error('[QA][refill]', e);
      window.alert('주사위 +10 실패: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setQaBusy(null);
    }
  }, [user, loadAll, qaBusy]);

  if (dataCorrupt) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={headerRow}>
            <h1 style={titleStyle}>
              {/* ISSUE-07: 상단 타이틀 영문 통일 — 중앙 주사위 상단 'nGle Marble' 과 표기 통일 */}
              <span style={{ color: '#00A6B5' }}>nGle</span>
              <span style={{ color: '#f0f0f0' }}> </span>
              <span style={{ color: '#106070' }}>Marble</span>
            </h1>
            <button style={backBtn} onClick={() => navigate('/tournament/games')}><ArrowLeft size={14} /> 게임센터</button>
          </div>
          <div style={corruptBox}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
            <h3 style={{ color: '#fca5a5', margin: '0 0 10px', fontSize: 18 }}>저장된 데이터에 문제가 발견되었습니다</h3>
            <p style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.7, margin: 0 }}>
              게임 상태를 안전하게 읽을 수 없어 플레이가 중단되었습니다.
              <br />원본 데이터는 보존되었으며, 자동으로 초기화하지 않습니다.
              <br />관리자에게 문의하거나 새로고침 후 다시 시도해 주세요.
            </p>
            <div style={{ marginTop: 16, fontSize: 11, color: '#64748b' }}>
              사용자 ID: <code>{user?.profile.id}</code>
            </div>
          </div>
          <LeaderboardMini rows={top5} myRank={myRank} onFull={() => navigate('/tournament/leaderboard-marble')} />
        </div>
      </div>
    );
  }
  // Web Locks 미지원 환경 fail-closed (사용자 지시 §2)
  if (user && !tabLock.isLockSupported) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={headerRow}>
            <h1 style={titleStyle}>
              {/* ISSUE-07: 상단 타이틀 영문 통일 */}
              <span style={{ color: '#00A6B5' }}>nGle</span>
              <span style={{ color: '#f0f0f0' }}> </span>
              <span style={{ color: '#106070' }}>Marble</span>
            </h1>
            <button style={backBtn} onClick={() => navigate('/tournament/games')}><ArrowLeft size={14} /> 게임센터</button>
          </div>
          <div style={corruptBox}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
            <h3 style={{ color: '#fca5a5', margin: '0 0 10px', fontSize: 18 }}>지원되지 않는 브라우저</h3>
            <p style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.7, margin: 0 }}>
              이 브라우저는 다중 탭 중복 플레이 방지에 필요한 기능 (Web Locks API) 을 지원하지 않습니다.
              <br />공정한 게임 진행을 위해 Chrome, Edge, Firefox, Safari 최신 버전을 사용해 주세요.
            </p>
          </div>
        </div>
      </div>
    );
  }
  if (phase === 'loading' || !user || !myState) {
    return <div style={pageStyle}><div style={{ color: '#94a3b8' }}>로드 중…</div></div>;
  }

  const displayPosition = animPos ?? myState.position;
  const highlightTarget = phase === 'animating' && lastRoll ? lastRoll.to : null;
  const isUnlimited = diceStatus?.mode === 'unlimited';
  const diceDisplayValue = diceStatus
    ? isUnlimited ? '∞' : `${diceStatus.remaining ?? 0} / ${diceStatus.limit ?? '-'}${diceStatus.weekendCarryover ? ' (주말·금요일 잔여)' : ''}`
    : `${myState.diceToday} / ${MARBLE_GLOBAL_CONFIG.diceDailyLimit}`;
  const canRollByQuota = isUnlimited || (diceStatus?.remaining ?? myState.diceToday) > 0;
  const canUseSpeed = myState.items.speedBoosts > 0 && !myState.isTrapped;

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {/* V70 2026-10-19 — 서버/Mock 분기: Flag ON 시 공식 서버 기록 안내 */}
        <div style={demoBanner}>
          {isPhase3cServerEnabled()
            ? '공식 리더보드에 반영되는 플레이 기록입니다'
            : '이 브라우저에만 저장되는 플레이 기록입니다'}
        </div>

        {!tabLock.isActive && (
          <div style={shadowBanner}>
            ⚠ 다른 탭에서 플레이 중 — 조회만 가능합니다. 플레이하려면 다른 탭을 종료해주세요.
          </div>
        )}

        <div style={headerRow}>
          <h1 style={titleStyle}>
            {/* ISSUE-07: 상단 타이틀 영문 통일 — 중앙 주사위 상단 'nGle Marble' 과 표기 통일 */}
            <span style={{ color: '#00A6B5' }}>nGle</span>
            <span style={{ color: '#f0f0f0' }}> </span>
            <span style={{ color: '#106070' }}>Marble</span>
          </h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={helpBtn} onClick={() => setShowHelp(true)}><HelpCircle size={14} /> 게임 방법</button>
            <button style={backBtn} onClick={() => navigate('/tournament/games')}><ArrowLeft size={14} /> 게임센터</button>
          </div>
        </div>

        <HUD state={myState} myRank={myRank} diceLabel={diceDisplayValue} />

        <SquareBoard
          cells={board}
          displayPosition={displayPosition}
          highlightTarget={highlightTarget}
          onRoll={handleRoll}
          phase={phase}
          state={myState}
          diceAnimValue={diceAnimValue}
          canRollByQuota={canRollByQuota}
          canUseSpeed={canUseSpeed}
          useSpeedBoost={useSpeedBoost}
          onToggleSpeed={() => setUseSpeedBoost(v => !v)}
          tabActive={tabLock.isActive}
        />

        {error && <div style={errorBox}>{error}</div>}

        {phase === 'escape-cheer' && escapeCheer && (
          <EscapeCheerOverlay cheer={escapeCheer} />
        )}

        {phase === 'lap-cheer' && lapCheer && (
          <LapRewardOverlay cheer={lapCheer} />
        )}

        {phase === 'trap-entered' && trapEntered && (
          <TrapEnteredOverlay cheer={trapEntered} />
        )}

        {trapFailToast && (
          <TrapFailToast toast={trapFailToast} />
        )}

        {phase === 'event' && activePending && user && (
          <MarbleEventModal
            key={activePending._instanceId}
            pending={activePending}
            board={board}
            previousScore={previousScore ?? myState.totalScore}
            currentScore={myState.totalScore}
            selfPosition={myState.position}
            shieldsOwned={myState.items.shields}
            onSubmit={async (choice) => {
              // 사용자 지시 §2: 모든 상태 변경 경로에서 lock 검증
              if (!tabLock.isActive) throw new Error('다른 탭에서 플레이 중입니다. 제어권을 가져온 후 다시 시도하세요.');
              if (dataCorrupt) throw new Error('저장된 데이터에 문제가 있어 처리할 수 없습니다.');
              const inst = activePending._instanceId;
              if (!choiceReqRef.current[inst]) {
                choiceReqRef.current[inst] = `choice-${inst}`;
              }
              const reqId = choiceReqRef.current[inst];
              const r = await marbleApi.submitEventChoice(user.profile.id, {
                clientRequestId: reqId, eventId: activePending.eventId, choice,
              });
              // V70G8K — 홀짝: 서버 확정 점수는 받아 두되, 결과 공개(onResultShown) 전까지 HUD·순위·리더보드에 반영하지 않음
              if (shouldDeferScoreReveal(activePending.kind)) {
                const held: DeferredReveal = { instanceId: inst };
                deferredRevealRef.current = held;
                try {
                  const [s, ds] = await Promise.all([
                    marbleApi.getMyState(user.profile.id, (user.profile as any).nameKo || user.profile.displayName || '', user.profile.orgUnitId),
                    marbleApi.getDailyDiceStatus(user.profile.id),
                  ]);
                  held.state = s;
                  held.diceStatus = ds;
                } catch { /* ignore — 공개 시 리더보드만 갱신 (기존과 동일하게 상태 재조회 실패는 무시) */ }
                return r;
              }
              try {
                const [s, ds] = await Promise.all([
                  marbleApi.getMyState(user.profile.id, (user.profile as any).nameKo || user.profile.displayName || '', user.profile.orgUnitId),
                  marbleApi.getDailyDiceStatus(user.profile.id),
                ]);
                setMyState(s);
                setDiceStatus(ds);
              } catch { /* ignore */ }
              void refreshLeaderboard();
              return r;
            }}
            onResultShown={() => {
              if (deferredRevealRef.current?.instanceId !== activePending._instanceId) return;
              applyDeferredReveal();
            }}
            onClose={() => {
              // V70G8K — 결과 공개 없이 닫히는 경우에도 보류분을 반영 (점수 불일치 방지)
              const applied = deferredRevealRef.current?.instanceId === activePending._instanceId
                ? applyDeferredReveal()
                : null;
              const nextInProgress = (applied ?? myState)?.inProgressEvent;
              const hasNext = !!nextInProgress && nextInProgress.eventId !== activePending.eventId;
              if (hasNext) {
                const nextInst: ActivePending = { ...nextInProgress, _instanceId: makeInstanceId() };
                setActivePending(nextInst);
                delete choiceReqRef.current[activePending._instanceId];
                return;
              }
              delete choiceReqRef.current[activePending._instanceId];
              setActivePending(null);
              handleNextRoll();
            }}
          />
        )}
        {phase === 'result' && lastRoll && user && (
          <MarbleEventModal
            pending={{ eventId: lastRoll.rollId, kind: lastRoll.cellKind, createdAt: Date.now() }}
            board={board}
            previousScore={previousScore ?? myState.totalScore}
            currentScore={myState.totalScore}
            selfPosition={myState.position}
            shieldsOwned={myState.items.shields}
            immediate={{
              kind: lastRoll.cellKind as 'safe' | 'E8' | 'E10',
              delta: lastRoll.immediate?.delta ?? 0,
              outcomeLabel: lastRoll.immediate?.label,
              shieldConsumed: lastRoll.immediate?.shieldConsumed,
              grossLoss: lastRoll.immediate?.grossLoss,
              shieldsCapReached: lastRoll.immediate?.shieldsCapReached,  // QA Issue #4
            }}
            onClose={handleNextRoll}
          />
        )}

        <LeaderboardMini rows={top5} myRank={myRank} onFull={() => navigate('/tournament/leaderboard-marble')} />

        <QaPanel onReset={handleQaReset} onRefill={handleQaRefill} busy={qaBusy} />

        {showHelp && <MarbleHelpModal onClose={() => setShowHelp(false)} />}
      </div>
    </div>
  );
}

// ──────── 탈출 Cheer 오버레이 (성공/실패/보장 공개 — 이동 전) ────────

function EscapeCheerOverlay({ cheer }: { cheer: EscapeCheerState }) {
  const { escaped, wasGuaranteed, diceFace, trapFailsAfter } = cheer;
  const bg = escaped ? 'linear-gradient(135deg, #065f46 0%, #10b981 100%)'
    : 'linear-gradient(135deg, #78350f 0%, #d97706 100%)';
  return (
    <div style={cheerBackdrop} className="mb-escape-cheer">
      <div style={{ ...cheerCard, background: bg }}>
        <div style={{ fontSize: 72, lineHeight: 1, marginBottom: 8 }}>
          {wasGuaranteed ? '🚁' : '🏝'}
        </div>
        <div style={{ fontSize: 26, fontWeight: 900, color: '#fff', letterSpacing: 1, textShadow: '0 2px 6px rgba(0,0,0,0.4)' }}>
          {wasGuaranteed ? '구조대 도착!' : escaped ? '탈출 성공!' : '탈출 실패'}
        </div>
        <div style={{ marginTop: 10, fontSize: 14, color: '#f8fafc', opacity: 0.95 }}>
          주사위 <b style={{ fontSize: 22, color: '#fde68a' }}>{diceFace}</b>
        </div>
        {wasGuaranteed && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#fef3c7' }}>
            (3회 보장 발동 — 자동 탈출)
          </div>
        )}
        {!escaped && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#fef3c7' }}>
            {trapFailsAfter}/3 시도 완료 — {trapFailsAfter >= 2 ? '다음 시도 보장!' : '1·2차 모두 실패 시 3차 보장'}
          </div>
        )}
      </div>
    </div>
  );
}

const cheerBackdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.6)',
  zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const cheerCard: React.CSSProperties = {
  padding: '32px 48px', borderRadius: 20, textAlign: 'center',
  border: '3px solid rgba(255,255,255,0.2)',
  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  minWidth: 240,
};

// ──────── 무인도 최초 진입 Overlay (QA4 §1 — 사용자 "확인" 눌러야 다음) ────────

function TrapEnteredOverlay({ cheer }: { cheer: TrapEnteredState }) {
  const { onContinue } = cheer;
  const btnRef = useRef<HTMLButtonElement | null>(null);
  React.useEffect(() => { btnRef.current?.focus(); }, []);
  return (
    <div style={cheerBackdrop} className="mb-escape-cheer" role="dialog" aria-label="무인도 진입">
      <div style={{ ...cheerCard, background: 'linear-gradient(135deg, #78350f 0%, #92400e 50%, #d97706 100%)', padding: '32px 40px', maxWidth: 400 }}>
        <div style={{ fontSize: 72, lineHeight: 1, marginBottom: 8 }}>🏝</div>
        <div style={{ fontSize: 24, fontWeight: 900, color: '#fff', letterSpacing: 1, textShadow: '0 2px 6px rgba(0,0,0,0.4)' }}>
          무인도에 갇혔습니다!
        </div>
        <div style={{
          marginTop: 16, padding: '12px 16px',
          background: 'rgba(0,0,0,0.3)', borderRadius: 10,
          textAlign: 'left', fontSize: 12, lineHeight: 1.7, color: '#fef3c7',
        }}>
          <div style={{ marginBottom: 4 }}>• 다음 턴부터 탈출 시도 가능</div>
          <div style={{ marginBottom: 4 }}>• 첫 {TRAP_CONFIG.guaranteeAfterFails}회 시도: 주사위 <b style={{ color: '#fff' }}>{TRAP_CONFIG.successThreshold}·{TRAP_CONFIG.successThreshold + 1}·{TRAP_CONFIG.successThreshold + 2}</b>이면 탈출 성공</div>
          <div>• {TRAP_CONFIG.guaranteeAfterFails + 1}번째 시도: 주사위 값과 관계없이 <b style={{ color: '#fff' }}>무조건 탈출</b></div>
        </div>
        <button
          ref={btnRef}
          onClick={onContinue}
          aria-label="확인"
          style={{
            marginTop: 20, padding: '10px 32px', display: 'inline-block',
            background: '#fff', color: '#78350f',
            fontWeight: 800, border: 0, borderRadius: 10, cursor: 'pointer',
            fontSize: 15, letterSpacing: 0.5,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            outline: 'none',
          }}
          onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 3px #fde68a, 0 4px 12px rgba(0,0,0,0.3)'; }}
          onBlur={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)'; }}
        >
          확인
        </button>
      </div>
    </div>
  );
}

// ──────── 탈출 실패 토스트 (QA4 §2 — 모달 X, 자동 사라짐) ────────

function TrapFailToast({ toast }: { toast: TrapFailToastState }) {
  const { diceFace, trapFailsAfter } = toast;
  // §QA4 §2 사용자 지시 문구 (실제 상태값 기준):
  // 1차 실패 (trapFailsAfter=1): "탈출 실패! 다음은 2번째 시도입니다."
  // 2차 실패 (trapFailsAfter=2): "탈출 실패! 다음 3번째 시도에는 무조건 탈출합니다."
  const nextAttempt = trapFailsAfter + 1;
  const isGuaranteedNext = trapFailsAfter >= TRAP_CONFIG.guaranteeAfterFails;
  const message = isGuaranteedNext
    ? `다음 ${nextAttempt}번째 시도에는 무조건 탈출합니다.`
    : `다음은 ${nextAttempt}번째 시도입니다.`;
  return (
    <div
      role="status" aria-live="polite"
      style={{
        position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)',
        zIndex: 998,
        padding: '12px 20px', borderRadius: 10,
        background: 'linear-gradient(135deg, #78350f 0%, #d97706 100%)',
        border: '2px solid #fef3c7',
        color: '#fff', fontWeight: 700, fontSize: 13,
        boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
        animation: 'mb-toast-slide 2500ms ease-in-out both',
        maxWidth: '90vw', textAlign: 'center',
      }}
    >
      <span style={{ fontSize: 18, marginRight: 6 }}>🏝</span>
      탈출 실패! <span style={{ color: '#fde68a', margin: '0 4px' }}>(주사위 {diceFace})</span> {message}
    </div>
  );
}

// ──────── 바퀴 완주 Cheer 오버레이 (QA3 §1 — 사용자 "계속하기" 눌러야 다음 이벤트로) ────────

function LapRewardOverlay({ cheer }: { cheer: LapCheerState }) {
  const { amount, laps, onContinue } = cheer;
  const btnRef = useRef<HTMLButtonElement | null>(null);
  // 키보드 접근성: 자동 포커스 + Enter/Space 트리거 (기본 button 거동)
  React.useEffect(() => { btnRef.current?.focus(); }, []);
  return (
    <div style={cheerBackdrop} className="mb-escape-cheer" role="dialog" aria-label="바퀴 완주 보너스">
      <div style={{ ...cheerCard, background: 'linear-gradient(135deg, #92400e 0%, #d97706 50%, #fbbf24 100%)', padding: '36px 48px' }}>
        <div style={{ fontSize: 72, lineHeight: 1, marginBottom: 8 }}>🏁</div>
        <div style={{ fontSize: 26, fontWeight: 900, color: '#fff', letterSpacing: 1, textShadow: '0 2px 6px rgba(0,0,0,0.4)' }}>
          바퀴 완주!
        </div>
        <div style={{ marginTop: 12, fontSize: 44, fontWeight: 900, color: '#fef3c7', textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
          +{amount.toLocaleString()}점
        </div>
        {laps >= 2 && (
          <div style={{ marginTop: 4, fontSize: 13, color: '#fde68a', fontWeight: 700 }}>
            {laps} 바퀴 완주
          </div>
        )}
        <button
          ref={btnRef}
          onClick={onContinue}
          aria-label="계속하기"
          style={{
            marginTop: 20, padding: '10px 28px', display: 'inline-block',
            background: '#fff', color: '#92400e',
            fontWeight: 800, border: 0, borderRadius: 10, cursor: 'pointer',
            fontSize: 15, letterSpacing: 0.5,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            outline: 'none',
          }}
          onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 3px #fde68a, 0 4px 12px rgba(0,0,0,0.3)'; }}
          onBlur={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)'; }}
        >
          계속하기 →
        </button>
      </div>
    </div>
  );
}

// ──────── HUD ────────

function HUD({ state, myRank, diceLabel }: { state: MyState; myRank: number | null; diceLabel: string }) {
  return (
    <div style={hudRow}>
      <HudItem icon={<Dices size={14} />} label="주사위" value={diceLabel} tone="cyan" />
      <HudItem icon={<Trophy size={14} />} label="누적 점수" value={state.totalScore.toLocaleString()} tone="yellow" big />
      <HudItem icon={<Flag size={14} />} label="바퀴" value={`×${state.lapsCompleted}`} tone="green" />
      <HudItem icon={null} label="완주 보상" value={`+${state.lapsReward.toLocaleString()}`} tone="green" />
      <HudItem icon={<Shield size={14} />} label="방패" value={`×${state.items.shields}`} tone="violet" />
      <HudItem icon={<Zap size={14} />} label="가속" value={`×${state.items.speedBoosts}`} tone="magenta" />
      <HudItem icon={null} label="순위" value={myRank != null ? `${myRank}위` : '-'} tone="muted" />
      <HudItem icon={null} label="위치" value={`${state.position + 1}/24`} tone="muted" />
      {state.isTrapped && (
        <HudItem icon={null} label="🏝 무인도" value={trapAttemptHudValue(state.trapFails)} tone="magenta" />
      )}
    </div>
  );
}

function HudItem({ icon, label, value, tone, big }: { icon: React.ReactNode; label: string; value: React.ReactNode; tone: 'cyan'|'yellow'|'violet'|'magenta'|'muted'|'green'; big?: boolean }) {
  // V70 2026-10-19 — NGLE 브랜드 Teal 재디자인: Primary Teal + Gold 중심.
  //   cyan→블루그레이, yellow→샌드, violet→세이지, magenta→블루그레이, green→세이지.
  const colorMap = { cyan:'#00A6B5', yellow:'#F5C842', violet:'#9CDDE2', magenta:'#F5883A', muted:'#65686B', green: '#00A6B5' };
  return (
    <div style={hudItemStyle}>
      <div style={{ color: '#65686B', fontSize: 10, display:'flex', alignItems:'center', gap: 4, whiteSpace: 'nowrap' }}>
        {icon && <span style={{ color: colorMap[tone] }}>{icon}</span>} {label}
      </div>
      <div style={{ color: colorMap[tone], fontWeight: 700, fontSize: big ? 20 : 14, whiteSpace: 'nowrap' }} data-testid={`hud-${label}`}>{value}</div>
    </div>
  );
}

// ──────── 사각 보드 ────────

function SquareBoard({
  cells, displayPosition, highlightTarget, onRoll, phase, state, diceAnimValue, canRollByQuota, canUseSpeed, useSpeedBoost, onToggleSpeed, tabActive,
}: {
  cells: BoardCell[];
  displayPosition: number;
  highlightTarget: number | null;
  onRoll: () => void;
  phase: Phase;
  state: MyState;
  diceAnimValue: number | null;
  canRollByQuota: boolean;
  canUseSpeed: boolean;
  useSpeedBoost: boolean;
  onToggleSpeed: () => void;
  tabActive: boolean;
}) {
  const canRoll = tabActive && phase === 'ready' && canRollByQuota && !state.inProgressEvent;
  const btnLabel = state.isTrapped
    ? trapAttemptButtonLabel(state.trapFails)
    : phase === 'animating' ? '이동 중…'
    : canRoll ? '🎲 주사위 굴리기'
    : state.inProgressEvent ? '이벤트 진행 중'
    : !tabActive ? '다른 탭 플레이 중'
    : '주사위 소진';

  return (
    <div style={boardOuterWrap}>
      <div style={boardSquareFrame}>
        <div style={boardSquareInner}>
          {cells.map((c) => {
            const { row, col } = positionToGrid(c.index);
            const isMe = c.index === displayPosition;
            const isTarget = c.index === highlightTarget && !isMe;
            const isCorner = [0, 6, 12, 18].includes(c.index);
            const label = c.kind === 'safe' ? safeCornerLabel(c.index) : cellLabel(c.kind);
            const fg = cellForeground(c.kind, c.index);
            const fgSoft = cellForegroundSoft(c.kind, c.index);
            return (
              <div
                key={c.index}
                role="gridcell"
                aria-label={`칸 ${c.index + 1}, 이벤트: ${label}`}
                className={isMe ? 'marble-cell active' : 'marble-cell'}
                style={{
                  gridRow: row + 1,
                  gridColumn: col + 1,
                  background: cellBackground(c.kind, c.index, isCorner),
                  border: isMe ? '2px solid #fbbf24' : isTarget ? '2px dashed #fcd34d' : '1px solid rgba(99,102,241,0.3)',
                  boxShadow: isMe
                    ? COLORS.shadowCellActive
                    : COLORS.shadowCell,
                  transform: isMe ? 'scale(1.08)' : 'scale(1)',
                  transition: 'transform 180ms cubic-bezier(0.2,0.8,0.3,1), box-shadow 180ms ease, border-color 180ms ease',
                  color: fg,
                  zIndex: isMe ? 2 : 1,
                  ...cellBaseStyle,
                }}
                title={`${c.index + 1} · ${label}`}
              >
                <div style={{ fontSize: 10, opacity: 0.75, color: fgSoft, lineHeight: 1, position: 'absolute', top: 3, left: 4 }}>{c.index + 1}</div>
                <div style={{ marginTop: 4, filter: isCorner ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' : 'none' }}>
                  <CellIcon kind={c.kind} index={c.index} size={32} />
                </div>
                <div style={{ fontSize: 11, opacity: 0.95, fontWeight: 700, textAlign: 'center', lineHeight: 1.1, color: fg, marginTop: 2 }}>
                  {label}
                </div>
                {isMe && <PlayerPawn variant={state.isTrapped ? 'trapped' : 'normal'} />}
              </div>
            );
          })}
          <div style={centerPanel}>
            <div style={centerTitle}>
              <span style={{ color: '#00A6B5' }}>nGle</span>
              <span style={{ color: '#f0f0f0' }}> </span>
              <span style={{ color: '#106070' }}>Marble</span>
            </div>
            <div style={centerSub}>24칸 보드 게임</div>

            <div style={centerDiceBox}>
              <Dice3D value={diceAnimValue} spinning={phase === 'animating' && diceAnimValue == null} size={72} />
            </div>

            {canUseSpeed && !state.isTrapped && (
              <label style={speedToggleLabel}>
                <input type="checkbox" checked={useSpeedBoost} onChange={onToggleSpeed} disabled={!canRoll} />
                <Zap size={13} style={{ verticalAlign: 'middle', color: '#00A6B5' }} /> 가속 사용 (주사위 +2)
              </label>
            )}

            <button
              style={rollBtn(canRoll)}
              disabled={!canRoll}
              onClick={onRoll}
            >
              {btnLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────── 배경 색 ────────

// V70 2026-10-19 — 프리미엄 보드게임 팔레트 적용.
//   24칸 색상을 4 톤 variation 으로 단순화. 특수칸 식별은 아이콘/라벨 중심.
//   네온 녹색/노랑/보라/핑크/시안/빨강/인디고 전부 제거.
// V70 2026-10-19 — NGLE 브랜드 Teal 재디자인.
//   보드 24칸 밝은 teal/화이트 베이스. 특수칸만 Gold/Primary Teal 로 명확 구분.
function cellBackground(kind: string, index: number, isCorner: boolean): string {
  // 특수 안전 셀 — 명확한 강조
  if (index === V2_SAFE_ROLES.START) return 'linear-gradient(135deg, #F5C842 0%, #FFE07A 100%)';  // Gold (출발)
  if (index === V2_SAFE_ROLES.TRAP) return 'linear-gradient(135deg, #00A6B5 0%, #2BBECC 100%)';   // Primary Teal (강조)
  if (index === V2_SAFE_ROLES.VAULT) return 'linear-gradient(135deg, #00A6B5 0%, #2BBECC 100%)';  // Primary Teal
  if (index === V2_SAFE_ROLES.SHOP) return 'linear-gradient(135deg, #9CDDE2 0%, #B8E8EC 100%)';   // Light Teal (상점)
  // 일반 safe 셀 — Mist Teal variation (코너는 살짝 더 밝음)
  if (kind === 'safe' && isCorner) return 'linear-gradient(135deg, #DFF2F4 0%, #E8F5F7 100%)';
  if (kind === 'safe') return 'linear-gradient(135deg, #E8F5F7 0%, #F5FBFC 100%)';
  // 이벤트 셀 — teal variation 중심 (아이콘/라벨로 구분)
  if (kind === 'E10') return 'linear-gradient(135deg, #F5C842 0%, #FFE07A 100%)';   // Gold (잭팟)
  if (kind === 'E8') return 'linear-gradient(135deg, #00A6B5 0%, #2BBECC 100%)';     // Primary Teal (방패)
  if (kind === 'E9') return 'linear-gradient(135deg, #9CDDE2 0%, #B8E8EC 100%)';     // Light Teal (워프)
  if (kind === 'E5') return 'linear-gradient(135deg, #DFF2F4 0%, #E8F5F7 100%)';     // Mist (폭탄)
  if (kind === 'E3') return 'linear-gradient(135deg, #00A6B5 0%, #2BBECC 100%)';     // Primary Teal (보물상자)
  return 'linear-gradient(135deg, #F5FBFC 0%, #FFFFFF 100%)';                        // 기본 화이트/mist
}

/** 셀 글씨/아이콘 대비 색상 — 밝은 배경 위 어두운 텍스트 (WCAG AA) */
function cellForeground(kind: string, index: number): string {
  if (index === V2_SAFE_ROLES.START) return '#5C4A1A';   // Gold → dark brown-gold
  if (index === V2_SAFE_ROLES.TRAP) return '#FFFFFF';    // Primary Teal → white
  if (index === V2_SAFE_ROLES.VAULT) return '#FFFFFF';
  if (index === V2_SAFE_ROLES.SHOP) return '#106070';    // Light Teal → Deep Teal
  if (kind === 'E10') return '#5C4A1A';
  if (kind === 'E3') return '#FFFFFF';
  if (kind === 'E8') return '#FFFFFF';
  if (kind === 'E9') return '#106070';
  return '#222324';  // 메인 화이트 셀 위 다크 텍스트
}
/** 셀 번호 등 보조 텍스트용 — 메인 글씨보다 약간 흐림 */
function cellForegroundSoft(kind: string, index: number): string {
  if (index === V2_SAFE_ROLES.START) return '#8C7A4A';
  if (index === V2_SAFE_ROLES.TRAP) return '#DFF2F4';
  if (index === V2_SAFE_ROLES.VAULT) return '#DFF2F4';
  if (index === V2_SAFE_ROLES.SHOP) return '#1A8090';
  if (kind === 'E10') return '#8C7A4A';
  if (kind === 'E3') return '#DFF2F4';
  if (kind === 'E8') return '#DFF2F4';
  if (kind === 'E9') return '#1A8090';
  return '#65686B';
}

// ──────── 리더보드 미니 + QA ────────

function LeaderboardMini({ rows, myRank, onFull }: { rows: LeaderboardRow[]; myRank: number | null; onFull: () => void }) {
  return (
    <div style={lbSection}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
          <Trophy size={14} style={{ verticalAlign: 'middle', color: '#fbbf24', marginRight: 4 }} />
          엔글 마블 TOP 5
          {myRank != null && <span style={{ color: '#94a3b8', fontWeight: 500, marginLeft: 10, fontSize: 12 }}>내 순위: {myRank}위</span>}
        </h3>
        <button style={ghostBtn} onClick={onFull}>전체 보기</button>
      </div>
      {rows.length === 0 && <div style={{ color: '#64748b', padding: 10, textAlign: 'center' }}>아직 기록이 없습니다.</div>}
      {rows.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={lbTh}>순위</th>
              <th style={lbTh}>이름</th>
              <th style={{ ...lbTh, textAlign: 'right' }}>점수</th>
              <th style={{ ...lbTh, textAlign: 'right' }}>횟수</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.uid}>
                <td style={{ ...lbTd, color: r.rank <= 3 ? '#fbbf24' : '#94a3b8', fontWeight: 700 }}>{r.rank}</td>
                <td style={{ ...lbTd, fontWeight: 600 }}>{r.displayName}</td>
                <td style={{ ...lbTd, textAlign: 'right', fontFamily: 'ui-monospace, Menlo, Consolas, monospace', color: '#fbbf24', fontWeight: 700 }}>{r.totalScore.toLocaleString()}</td>
                <td style={{ ...lbTd, textAlign: 'right', color: '#64748b' }}>{r.playCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function QaPanel({ onReset, onRefill, busy }: { onReset: () => void; onRefill: () => void; busy: null | 'reset' | 'refill' }) {
  const [open, setOpen] = useState(false);
  // 운영 project 에서는 QA 패널 자체를 숨긴다. (BE 런타임 가드가 2차 방어)
  if (process.env.REACT_APP_FIREBASE_PROJECT_ID === 'ngle-works') return null;
  const disabled = busy != null;
  return (
    <div style={{ marginTop: 16, padding: 10, background: '#020617', border: '1px dashed #334155', borderRadius: 8 }}>
      <button onClick={() => setOpen(!open)} style={{ background: 'transparent', border: 0, color: '#94a3b8', fontSize: 12, cursor: 'pointer' }}>
        🧪 QA 테스트 패널 {open ? '▲' : '▼'}
      </button>
      {open && (
        <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={{ ...qaBtn, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }} onClick={onReset} disabled={disabled}>
            <RefreshCw size={13} /> {busy === 'reset' ? '리셋 중…' : '본인 상태 리셋'}
          </button>
          <button style={{ ...qaBtn, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }} onClick={onRefill} disabled={disabled}>
            <PlusCircle size={13} /> {busy === 'refill' ? '지급 중…' : '주사위 +10 (테스트)'}
          </button>
          <div style={{ color: '#64748b', fontSize: 11, flexBasis: '100%' }}>
            QA 전용 (ngle-worksqa). 운영 환경에서는 패널 자체가 숨겨지며 BE 호출도 permission-denied 로 거부됩니다.
          </div>
        </div>
      )}
    </div>
  );
}

// ──────── 스타일 — V70 2026-10-19 NGLE 브랜드 Teal 재디자인 ────────
//   공식 홈페이지(www.ngle.co.kr) + 로고 브랜드 컬러 중심.
//   밝은 teal/화이트 베이스 + Primary Teal (#00A6B5) 포인트 + Gold (#F5C842) 액센트.

const pageStyle: React.CSSProperties = {
  minHeight: '100vh', background: 'linear-gradient(180deg, #DFF2F4 0%, #C8E6E6 100%)',
  padding: 20, display: 'flex', justifyContent: 'center',
};
const cardStyle: React.CSSProperties = {
  width: '100%', maxWidth: 980,
  background: '#FFFFFF', border: '1px solid #DCDEE0', borderRadius: 16,
  padding: 20, color: '#222324',
  boxShadow: '0 8px 24px rgba(16, 96, 112, 0.1)',
};
const demoBanner: React.CSSProperties = {
  background: 'rgba(0, 166, 181, 0.08)', border: '1px solid rgba(0, 166, 181, 0.3)',
  color: '#106070', fontSize: 12, padding: '6px 10px', borderRadius: 6,
  marginBottom: 12, textAlign: 'center',
};
const shadowBanner: React.CSSProperties = {
  background: 'rgba(224, 64, 64, 0.08)', border: '1px solid rgba(224, 64, 64, 0.3)',
  color: '#B82A2A', fontSize: 13, padding: '8px 12px', borderRadius: 6,
  marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10,
};
const headerRow: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginBottom: 12, flexWrap: 'wrap', gap: 8,
};
const titleStyle: React.CSSProperties = {
  fontSize: 26, fontWeight: 800, margin: 0, letterSpacing: 1, color: '#222324',
};
const backBtn: React.CSSProperties = {
  padding: '6px 12px', background: 'transparent', border: '1px solid #DCDEE0',
  borderRadius: 6, color: '#65686B', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12,
};
const helpBtn: React.CSSProperties = {
  padding: '6px 12px', background: 'rgba(0, 166, 181, 0.1)', border: '1px solid #00A6B5',
  borderRadius: 6, color: '#106070', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12,
};
const hudRow: React.CSSProperties = {
  display: 'flex', background: '#F5FBFC', borderRadius: 8, border: '1px solid #DCDEE0',
  padding: '4px 0', marginBottom: 14, overflowX: 'auto',
};
const hudItemStyle: React.CSSProperties = {
  padding: '8px 10px', borderRight: '1px solid #DCDEE0', minWidth: 70,
};
const boardOuterWrap: React.CSSProperties = { display: 'flex', justifyContent: 'center', margin: '14px 0', width: '100%' };
const boardSquareFrame: React.CSSProperties = {
  width: '100%', maxWidth: 720, aspectRatio: '1',
  background: 'linear-gradient(135deg, #DFF2F4 0%, #FFFFFF 50%, #DFF2F4 100%)',
  borderRadius: 16, padding: 10,
  boxShadow: '0 8px 24px rgba(16, 96, 112, 0.15), inset 0 0 0 1px rgba(0, 166, 181, 0.1)',
  border: '2px solid #00A6B5',
};
const boardSquareInner: React.CSSProperties = {
  width: '100%', height: '100%',
  display: 'grid',
  gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
  gridTemplateRows: `repeat(${GRID_SIZE}, 1fr)`,
  gap: 4,
};
const cellBaseStyle: React.CSSProperties = {
  borderRadius: 6, position: 'relative',
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  textAlign: 'center', padding: 2, overflow: 'hidden',
};
// V70 2026-10-19 — 중앙 패널 가독성 개선 (사용자 피드백: 진한 Teal 배경에서 제목 안 보임)
//   밝은 화이트/파스텔 teal 배경 + Primary/Deep Teal 제목 inline span (이미 적용됨)
//   부가 텍스트/아이콘은 다크/미디엄 톤으로 대비 확보.
const centerPanel: React.CSSProperties = {
  gridRow: '2 / 7', gridColumn: '2 / 7',
  background: 'linear-gradient(135deg, #FFFFFF 0%, #DFF2F4 100%)',
  borderRadius: 10, border: '1px solid #00A6B5',
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  padding: 12, color: '#222324',
  boxShadow: '0 2px 8px rgba(16, 96, 112, 0.1), inset 0 1px 0 rgba(255,255,255,0.6)',
};
const centerTitle: React.CSSProperties = {
  // inline span 이 "nGle"=Primary Teal + "Marble"=Deep Teal 적용. 흰색 공백 span 도 밝은 배경에 영향 無.
  fontSize: 26, fontWeight: 900, letterSpacing: 2, color: '#222324',
  textShadow: '0 1px 2px rgba(255, 255, 255, 0.6)',
};
const centerSub: React.CSSProperties = {
  color: '#65686B', fontSize: 11, marginTop: 4,
};
const centerDiceBox: React.CSSProperties = { marginTop: 14, display: 'flex', justifyContent: 'center' };
const speedToggleLabel: React.CSSProperties = {
  marginTop: 10, fontSize: 11, color: '#65686B',
  display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
};
const rollBtn = (enabled: boolean): React.CSSProperties => ({
  // Primary Teal CTA — 브랜드 포인트 컬러
  marginTop: 10, padding: '10px 14px', width: '100%',
  background: enabled ? '#F5C842' : '#DCDEE0',
  color: enabled ? '#5C4A1A' : '#878B8E',
  fontWeight: 800, fontSize: 13, border: 0, borderRadius: 8,
  cursor: enabled ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap',
  boxShadow: enabled ? '0 2px 8px rgba(245, 200, 66, 0.4)' : 'none',
  transition: 'background 150ms ease-out',
});
const errorBox: React.CSSProperties = {
  marginBottom: 12, padding: 10,
  background: 'rgba(224, 64, 64, 0.08)', border: '1px solid rgba(224, 64, 64, 0.3)',
  color: '#B82A2A', borderRadius: 6, fontSize: 13,
};
const corruptBox: React.CSSProperties = {
  marginTop: 20, marginBottom: 16, padding: 24,
  background: 'rgba(224, 64, 64, 0.06)',
  border: '1px solid rgba(224, 64, 64, 0.25)',
  color: '#B82A2A', borderRadius: 10, textAlign: 'center',
};
const lbSection: React.CSSProperties = {
  background: '#F5FBFC', border: '1px solid #DCDEE0', borderRadius: 10,
  padding: 12, marginTop: 16,
};
const lbTh: React.CSSProperties = {
  textAlign: 'left', padding: '6px 10px', color: '#65686B', fontSize: 11,
  fontWeight: 600, borderBottom: '1px solid #DCDEE0', textTransform: 'uppercase',
};
const lbTd: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid #DCDEE0', fontSize: 13, color: '#222324' };
const ghostBtn: React.CSSProperties = {
  background: 'transparent', border: 0, color: '#00A6B5', fontSize: 12, cursor: 'pointer', fontWeight: 600,
};
const qaBtn: React.CSSProperties = {
  padding: '6px 10px', background: '#F5FBFC', border: '1px solid #DCDEE0', borderRadius: 6,
  color: '#65686B', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12,
};

export default MarbleGamePage;
